import type {
  Kunde, KundeDetail, KundenGeraet, Besuch, Defekt,
  Ersatzteil, Offerte, FAQ, Dashboard, ServiceAuftrag
} from '../types'

const BASE = '/api'

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('vx_token')
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Fehler' }))
    throw new Error((err as { error?: string }).error ?? 'Unbekannter Fehler')
  }
  return res.json()
}

// ─── Auth ────────────────────────────────────────────────
export const auth = {
  login: (email: string, passwort: string) =>
    request<{ user: { id: string; name: string; email: string; rolle: string }; token: string }>(
      '/auth/login', { method: 'POST', body: JSON.stringify({ email, passwort }) }
    ),
  microsoftLogin: (accessToken: string) =>
    request<{ user: { id: string; name: string; email: string; rolle: string }; token: string }>(
      '/auth/microsoft', { method: 'POST', body: JSON.stringify({ access_token: accessToken }) }
    ),
}

// ─── Dashboard ───────────────────────────────────────────
export const dashboard = {
  get: () => request<Dashboard>('/dashboard'),
}

// ─── Kunden ──────────────────────────────────────────────
export const kunden = {
  list: () => request<Kunde[]>('/kunden'),
  get: (id: string) => request<KundeDetail>(`/kunden/${id}`),
  create: (data: Partial<Kunde>) =>
    request<Kunde>('/kunden', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Kunde>) =>
    request<Kunde>(`/kunden/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/kunden/${id}`, { method: 'DELETE' }),
  toggleFavorit: (id: string) =>
    request<{ favorit: number }>(`/kunden/${id}/favorit`, { method: 'PATCH' }),
}

// ─── Geräte ──────────────────────────────────────────────
export const geraete = {
  list: (kundeId?: string) =>
    request<KundenGeraet[]>(`/geraete${kundeId ? `?kunde_id=${kundeId}` : ''}`),
  get: (id: string) => request<KundenGeraet & { defekte: Defekt[] }>(`/geraete/${id}`),
  create: (data: Partial<KundenGeraet>) =>
    request<KundenGeraet>('/geraete', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<KundenGeraet>) =>
    request<KundenGeraet>(`/geraete/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/geraete/${id}`, { method: 'DELETE' }),
}

// ─── Besuche ─────────────────────────────────────────────
export const besuche = {
  list: (params?: { kunde_id?: string; status?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString()
    return request<Besuch[]>(`/besuche${q ? `?${q}` : ''}`)
  },
  get: (id: string) => request<Besuch & { defekte: Defekt[] }>(`/besuche/${id}`),
  create: (data: Partial<Besuch>) =>
    request<Besuch>('/besuche', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Besuch>) =>
    request<Besuch>(`/besuche/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/besuche/${id}`, { method: 'DELETE' }),
}

// ─── Defekte ─────────────────────────────────────────────
export const defekte = {
  list: (params?: { status?: string; kunde_id?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString()
    return request<Defekt[]>(`/defekte${q ? `?${q}` : ''}`)
  },
  get: (id: string) => request<Defekt>(`/defekte/${id}`),
  create: (data: Partial<Defekt>) =>
    request<Defekt>('/defekte', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Defekt>) =>
    request<Defekt>(`/defekte/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/defekte/${id}`, { method: 'DELETE' }),
}

// ─── Ersatzteile ─────────────────────────────────────────
export const ersatzteile = {
  list: () => request<Ersatzteil[]>('/ersatzteile'),
  create: (data: Partial<Ersatzteil>) =>
    request<Ersatzteil>('/ersatzteile', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Ersatzteil>) =>
    request<Ersatzteil>(`/ersatzteile/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/ersatzteile/${id}`, { method: 'DELETE' }),
}

// ─── Offerten ────────────────────────────────────────────
export const offerten = {
  list: () => request<Offerte[]>('/offerten'),
  get: (id: string) => request<Offerte>(`/offerten/${id}`),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  create: (data: Omit<Partial<Offerte>, 'positionen'> & { positionen?: any[] }) =>
    request<Offerte>('/offerten', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Offerte>) =>
    request<Offerte>(`/offerten/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/offerten/${id}`, { method: 'DELETE' }),
}

// ─── Service-Aufträge ────────────────────────────────────
export const serviceAuftraege = {
  list: (params?: { status?: string; kunde_id?: string }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString()
    return request<ServiceAuftrag[]>(`/service${q ? `?${q}` : ''}`)
  },
  get: (id: string) => request<ServiceAuftrag>(`/service/${id}`),
  create: (data: Partial<ServiceAuftrag>) =>
    request<ServiceAuftrag>('/service', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<ServiceAuftrag>) =>
    request<ServiceAuftrag>(`/service/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/service/${id}`, { method: 'DELETE' }),
}

// ─── Audit Log ───────────────────────────────────────────
export const auditLog = {
  list: (params?: { modul?: string; limit?: number }) => {
    const q = new URLSearchParams(params as Record<string, string>).toString()
    return request<{
      id: string; benutzer_name: string; aktion: string
      modul: string; bezeichnung: string; erstellt_am: string
    }[]>(`/audit-log${q ? `?${q}` : ''}`)
  },
}

// ─── Betriebsstunden ─────────────────────────────────────
export const betriebsstunden = {
  list: (geraetId: string) =>
    request<import('../types').BetriebsstundenEintrag[]>(`/geraete/${geraetId}/betriebsstunden`),
  create: (geraetId: string, data: { stunden: number; erfasst_am: string; notiz?: string }) =>
    request<import('../types').BetriebsstundenEintrag>(`/geraete/${geraetId}/betriebsstunden`, {
      method: 'POST', body: JSON.stringify(data),
    }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/betriebsstunden/${id}`, { method: 'DELETE' }),
}

// ─── Benutzer ────────────────────────────────────────────
export const benutzer = {
  list: () => request<import('../types').Benutzer[]>('/benutzer'),
  create: (data: { name: string; email: string; passwort: string; rolle: string }) =>
    request<import('../types').Benutzer>('/benutzer', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<import('../types').Benutzer> & { passwort?: string }) =>
    request<import('../types').Benutzer>(`/benutzer/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string) =>
    request<{ ok: boolean }>(`/benutzer/${id}`, { method: 'DELETE' }),
}

// ─── Bexio ───────────────────────────────────────────────
export const bexio = {
  syncArtikel: () =>
    request<{ ok: boolean; synced: number; added: number; updated: number }>(
      '/bexio/sync-artikel', { method: 'POST' }
    ),
  syncKunden: () =>
    request<{ ok: boolean; synced: number; added: number; updated: number }>(
      '/bexio/sync-kunden', { method: 'POST' }
    ),
}

// ─── FAQ ─────────────────────────────────────────────────
export const faqApi = {
  list: (modell?: string) =>
    request<FAQ[]>(`/faq${modell ? `?modell=${modell}` : ''}`),
  create: (data: Partial<FAQ>) =>
    request<FAQ>('/faq', { method: 'POST', body: JSON.stringify(data) }),
}
